<script>
import { ref, computed } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useLumerAI } from 'dashboard/composables/useLumerAI';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { LUMERAI_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';
import MacrosList from 'dashboard/routes/dashboard/conversation/Macros/List.vue';
import AiSummaries from 'dashboard/routes/dashboard/conversation/contact/AiSummaries.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
    MacrosList,
    AiSummaries,
    Popover,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
    isCopilotActive: {
      type: Boolean,
      default: false,
    },
    isInstagramCommentConversation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
      const isPrivate = mode === REPLY_EDITOR_MODES.NOTE;
      const replyAnnouncement = props.isInstagramCommentConversation
        ? 'Enviar Direct selecionado. A mensagem será enviada de forma privada ao autor do comentário.'
        : 'Resposta selecionada. A mensagem será enviada ao cliente.';
      window.dispatchEvent(
        new CustomEvent('a11y-announce', {
          detail: {
            message: isPrivate
              ? 'Nota interna selecionada. Somente a equipe verá esta nota.'
              : replyAnnouncement,
            priority: 'assertive',
          },
        })
      );
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { lumeraiTasksEnabled } = useLumerAI();
    const showCopilotMenu = ref(false);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(LUMERAI_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+Shift+KeyT': {
        action: event => {
          event.preventDefault();
          handleModeToggle();
        },
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    const store = useStore();
    const isCopilotVisible = computed(() => {
      const accountId = store.getters.getCurrentAccountId;
      const hasLumeraiTasks = store.getters[
        'accounts/isFeatureEnabledonAccount'
      ](accountId, FEATURE_FLAGS.LUMERAI_TASKS);
      return lumeraiTasksEnabled.value && hasLumeraiTasks;
    });

    const showAiSummaryButton = computed(() => {
      if (props.mode === REPLY_EDITOR_MODES.NOTE || props.isEditorDisabled)
        return false;
      return store.getters['accounts/isFeatureEnabledonAccount'](
        store.getters.getCurrentAccountId,
        FEATURE_FLAGS.LUMERAI_TASKS
      );
    });

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      lumeraiTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      showAiSummaryButton,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
      isCopilotVisible,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      :is-instagram-comment-conversation="isInstagramCommentConversation"
      @toggle-mode="handleModeToggle"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <div v-if="isCopilotVisible" class="relative">
        <NextButton
          ref="copilotToggleRef"
          ghost
          :disabled="disabled || isEditorDisabled"
          :class="{
            'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
            'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
          }"
          sm
          icon="i-ph-sparkle-fill"
          :aria-label="$t('CONVERSATION.REPLYBOX.LUMERAI_ACTIONS')"
          @click="toggleCopilotMenu"
        />
        <CopilotMenuBar
          v-if="showCopilotMenu"
          v-on-click-outside="[
            handleClickOutside,
            { ignore: [copilotToggleRef] },
          ]"
          :has-selection="false"
          :has-content="hasContent"
          :conversation-id="conversationId"
          class="ltr:right-0 rtl:left-0 bottom-full mb-2"
          @execute-copilot-action="handleCopilotAction"
        />
      </div>
      <div class="relative flex items-center">
        <Popover align="end" disable-mobile-view>
          <template #default="{ isOpen, toggle }">
            <NextButton
              v-tooltip.top-end="'Macros'"
              icon="i-ph-lightning"
              :variant="isOpen ? 'solid' : 'ghost'"
              :class="isOpen ? 'bg-n-slate-3' : 'text-n-slate-11'"
              sm
              :aria-pressed="isOpen"
              :aria-label="$t('CONVERSATION.REPLYBOX.MACROS')"
              @click="toggle"
            />
          </template>
          <template #content="{ hide }">
            <div class="w-80 max-h-[22rem] flex flex-col">
              <div
                class="flex justify-between items-center px-4 py-3 border-b border-n-slate-3 dark:border-n-slate-6 sticky top-0 bg-n-alpha-white dark:bg-n-solid-2"
              >
                <span class="font-semibold text-sm text-n-slate-12">{{
                  $t('MACROS.HEADER')
                }}</span>
                <button
                  type="button"
                  :aria-label="$t('CONVERSATION.REPLYBOX.CLOSE')"
                  class="text-n-slate-10 hover:text-n-slate-12"
                  @click="hide"
                >
                  <span aria-hidden="true" class="i-lucide-x size-4" />
                </button>
              </div>
              <MacrosList :conversation-id="conversationId" />
            </div>
          </template>
        </Popover>
      </div>

      <div v-if="showAiSummaryButton" class="relative flex items-center">
        <Popover align="end" disable-mobile-view>
          <template #default="{ isOpen, toggle }">
            <NextButton
              v-tooltip.top-end="'Resumos da IA'"
              icon="i-ph-brain"
              :variant="isOpen ? 'solid' : 'ghost'"
              :class="isOpen ? 'bg-n-slate-3' : 'text-n-slate-11'"
              sm
              :aria-pressed="isOpen"
              :aria-label="$t('CONVERSATION.REPLYBOX.AI_SUMMARIES')"
              @click="toggle"
            />
          </template>
          <template #content="{ hide }">
            <div class="w-80 max-h-[22rem] flex flex-col">
              <div
                class="flex justify-between items-center px-4 py-3 border-b border-n-slate-3 dark:border-n-slate-6 sticky top-0 bg-n-alpha-white dark:bg-n-solid-2"
              >
                <span class="font-semibold text-sm text-n-slate-12">{{
                  $t('CONTACT_PANEL.AI_SUMMARIES.HEADER')
                }}</span>
                <button
                  type="button"
                  :aria-label="$t('CONVERSATION.REPLYBOX.CLOSE')"
                  class="text-n-slate-10 hover:text-n-slate-12"
                  @click="hide"
                >
                  <span aria-hidden="true" class="i-lucide-x size-4" />
                </button>
              </div>
              <AiSummaries :conversation-id="conversationId" />
            </div>
          </template>
        </Popover>
      </div>

      <NextButton
        v-if="!isCopilotActive"
        ghost
        class="text-n-slate-11"
        sm
        icon="i-lucide-maximize-2"
        :aria-label="$t('CONVERSATION.REPLYBOX.TOGGLE_EDITOR_SIZE')"
        @click="$emit('toggleEditorSize')"
      />
    </div>
  </div>
</template>
